import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { SecurityPinProvider } from './context/SecurityPinContext';
import { KeyboardProvider } from './context/KeyboardContext';
import { VirtualKeyboard } from './components/common/VirtualKeyboard';

import { AppShell } from './components/layout/AppShell';
import { PwaInstallPrompt } from './components/common/PwaInstallPrompt';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { CashProvider } from './context/CashContext';
import { CashGate } from './components/cash/CashGate';
import AccessFlow from './pages/AccessFlow';
import { getSessionToken } from './lib/authStorage';
import TablesPage from './pages/TablesPage';
import FunzioniPage from './pages/FunzioniPage';
import OrderPage from './pages/OrderPage';
import MenuPage from './pages/MenuPage';
import KdsPage from './pages/KdsPage';
import BarPage from './pages/BarPage';
import PaymentsPage from './pages/PaymentsPage';
import InventoryPage from './pages/InventoryPage';
import StoreSalePage from './pages/StoreSalePage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import RemoteSupportPage from './pages/RemoteSupportPage';
import WaiterApp from './pages/waiter/WaiterApp';
import KitchenApp from './pages/kitchen/KitchenApp';
import CourierApp from './pages/CourierApp';
import CartaDigitalePage from './pages/CartaDigitalePage';
import DigitalMenuPage from './pages/DigitalMenuPage';
import TrackingPage from './pages/TrackingPage';
import AsportoDomicilioPage from './pages/AsportoDomicilioPage';
import LoyaltyPage from './pages/LoyaltyPage';
import PublicLoyaltyCard from './pages/PublicLoyaltyCard';
import GiftCardsPage from './pages/GiftCardsPage';
import PublicGiftCard from './pages/PublicGiftCard';
import DiscountsPage from './pages/DiscountsPage';
import ReservationsPage from './pages/ReservationsPage';
import PublicBooking from './pages/public/PublicBooking';
import CapturaMenuPage from './pages/public/CapturaMenuPage';
import ReceiptLanding from './pages/public/ReceiptLanding';
import GestoreLogin from './pages/gestore/GestoreLogin';
import GestoreApp from './pages/gestore/GestoreApp';

const Splash = () => null;

const RequireAuth = ({ children }) => {
  const token = getSessionToken();
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

const RoleHome = () => {
  const { user } = useAuth();
  if (user?.role === 'waiter') return <Navigate to="/waiter" replace />;
  if (user?.role === 'kitchen') return <Navigate to="/cucina-display" replace />;
  if (user?.role === 'corriere') return <Navigate to="/corriere" replace />;
  return <Navigate to="/tavoli" replace />;
};

// Operators-only shell: redirect role-specific dedicated apps away from the
// admin shell (waiter -> mobile app, kitchen -> full-screen KDS).
const OperatorOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'waiter') return <Navigate to="/waiter" replace />;
  if (user?.role === 'kitchen') return <Navigate to="/cucina-display" replace />;
  if (user?.role === 'corriere') return <Navigate to="/corriere" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AccessFlow />} />
      <Route path="/m/:restaurantId" element={<DigitalMenuPage channel="menu" />} />
      <Route path="/ordina/:restaurantId" element={<DigitalMenuPage channel="order" />} />
      <Route path="/track/:token" element={<TrackingPage />} />
      <Route path="/carta/fedelta/:code" element={<PublicLoyaltyCard />} />
      <Route path="/carta/regalo/:code" element={<PublicGiftCard />} />
      <Route path="/prenota/:rid" element={<PublicBooking />} />
      <Route path="/cattura/:token" element={<CapturaMenuPage />} />
      <Route path="/recibo/:orderId" element={<ReceiptLanding />} />
      <Route path="/waiter/*" element={<RequireAuth><CashProvider><CashGate><WaiterApp /></CashGate></CashProvider></RequireAuth>} />
      <Route path="/corriere/*" element={<RequireAuth><CourierApp /></RequireAuth>} />
      <Route path="/cucina-display" element={<RequireAuth><CashProvider><CashGate><KitchenApp /></CashGate></CashProvider></RequireAuth>} />
      <Route element={<RequireAuth><OperatorOnly><CashProvider><CashGate><AppShell /></CashGate></CashProvider></OperatorOnly></RequireAuth>}>
        <Route path="/" element={<Navigate to="/tavoli" replace />} />
        <Route path="/tavoli" element={<TablesPage />} />
        <Route path="/funzioni" element={<FunzioniPage />} />
        <Route path="/ordine/:tableId" element={<OrderPage />} />
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/carta-digitale" element={<CartaDigitalePage />} />
        <Route path="/cucina" element={<KdsPage />} />
        <Route path="/bar" element={<BarPage />} />
        <Route path="/pagamenti" element={<PaymentsPage />} />
        <Route path="/asporto" element={<AsportoDomicilioPage />} />
        <Route path="/magazzino" element={<InventoryPage />} />
        <Route path="/negozio/vendita" element={<StoreSalePage />} />
        <Route path="/report" element={<ReportsPage />} />
        <Route path="/utenti" element={<UsersPage />} />
        <Route path="/fedelta" element={<LoyaltyPage />} />
        <Route path="/buoni" element={<GiftCardsPage />} />
        <Route path="/sconti" element={<DiscountsPage />} />
        <Route path="/prenotazioni" element={<ReservationsPage />} />
        <Route path="/impostazioni" element={<SettingsPage />} />
        <Route path="/assistenza-remota" element={<RemoteSupportPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Root() {
  // The Gestore (super-admin) portal and the operational app are directly
  // reachable. License control is enforced per-restaurant at login (auth),
  // so no device-level activation page is required.
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/gestore/login" element={<GestoreLogin />} />
        <Route path="/gestore/*" element={<GestoreApp />} />
        <Route path="/*" element={<AppRoutes />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ConfirmProvider>
            <SecurityPinProvider>
              <KeyboardProvider>
                <Root />
                <Toaster position="top-right" richColors />
                <PwaInstallPrompt />
                <VirtualKeyboard />
              </KeyboardProvider>
            </SecurityPinProvider>
          </ConfirmProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
